import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';

interface MermaidDiagramProps {
  chart: string;
}

let mermaidInitialized = false;

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const renderIdRef = useRef<string>('');

  useEffect(() => {
    let isMounted = true;

    const renderDiagram = async () => {
      if (!containerRef.current || !chart) return;

      try {
        setError(null);
        setLoading(true);

        // Dynamically import mermaid
        const { default: mermaid } = await import('mermaid');

        // Initialize mermaid only once
        if (!mermaidInitialized) {
          mermaid.initialize({
            startOnLoad: false,
            theme: 'neutral',
            securityLevel: 'loose',
            themeVariables: {
              primaryColor: '#fff',
              primaryTextColor: '#333',
              primaryBorderColor: '#ccc',
              lineColor: '#333',
              secondaryColor: '#f0f0f0',
              background: '#fff',
              mainBkg: '#fff',
              secondBkg: '#f0f0f0',
            },
            flowchart: {
              htmlLabels: true,
              curve: 'basis',
            },
          });
          mermaidInitialized = true;
        }

        // Generate a unique ID for this render
        const newId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Only proceed if component is still mounted
        if (!isMounted) return;

        // Remove any existing diagram
        if (renderIdRef.current && document.getElementById(renderIdRef.current)) {
          const oldElement = document.getElementById(renderIdRef.current);
          oldElement?.remove();
        }

        // Store the new ID
        renderIdRef.current = newId;

        try {
          // Render the diagram
          const { svg } = await mermaid.render(newId, chart);

          // Only update DOM if component is still mounted
          if (isMounted && containerRef.current) {
            containerRef.current.innerHTML = svg;
            setLoading(false);
          }
        } catch (renderError: unknown) {
          // Clean up the error element that mermaid creates
          const errorElement = document.getElementById(newId);
          if (errorElement) {
            errorElement.remove();
          }

          if (isMounted) {
            // eslint-disable-next-line no-console
            console.error('Mermaid rendering error:', renderError);
            setError(
              renderError instanceof Error ? renderError.message : 'Failed to render diagram'
            );
            setLoading(false);

            // Show the source for debugging
            if (containerRef.current) {
              const pre = document.createElement('pre');
              pre.style.cssText =
                'text-align: left; color: #666; font-size: 12px; white-space: pre-wrap; margin: 0;';
              pre.textContent = chart;
              containerRef.current.innerHTML = '';
              containerRef.current.appendChild(pre);
            }
          }
        }
      } catch (err: unknown) {
        if (isMounted) {
          // eslint-disable-next-line no-console
          console.error('Mermaid component error:', err);
          setError(err instanceof Error ? err.message : 'Failed to load mermaid');
          setLoading(false);
        }
      }
    };

    // Render with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(renderDiagram, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);

      // Clean up any rendered diagram when component unmounts
      if (renderIdRef.current && document.getElementById(renderIdRef.current)) {
        const element = document.getElementById(renderIdRef.current);
        element?.remove();
      }
    };
  }, [chart]);

  return (
    <Paper
      elevation={1}
      sx={{
        my: 3,
        p: 3,
        overflow: 'auto',
        backgroundColor: '#fafafa',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        position: 'relative',
        minHeight: loading ? '200px' : 'auto',
        '& svg': {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          margin: '0 auto',
        },
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Loading diagram...
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color='error' variant='body2' gutterBottom>
            Diagram rendering error:
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {error}
          </Typography>
        </Box>
      )}

      <Box ref={containerRef} />
    </Paper>
  );
};

export default MermaidDiagram;
